import React, { useState, useEffect } from 'react';
import config from '../config';
import { Link } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';

const ManageProducts = ({ testMode = false }) => {

  // const host = testMode ? config.testBackendUrl : config.backendUrl;
  const host = config.backendUrl;

  const [products, setProducts] = useState([]);
  const [authToken, setAuthToken] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getAuthToken = async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens.idToken.toString();
        setAuthToken(token);
      } catch (error) {
        console.error('Error getting auth token:', error);
      }
    };
    
    getAuthToken();
  }, []);

  useEffect(() => {
    if (!authToken) return; // Don't fetch if no token yet

    setLoading(true);
    fetch(`${host}/api/admin/products`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      }
    })
      .then(response => {
        if (!response.ok) {
          console.log(response);
          // throw new Error('Network response was not ok');
          // console.error('Error fetching products:', response.statusText);
        }
        return response.json();
      })
      .then(data => {
        setProducts(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching products:', error);
        setLoading(false);
      });
  }, [authToken]);

  return (
    <div className="main-content">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-header-title">
            Manage Products
            {testMode && <span style={{color: 'var(--warning-color)'}}> (Test Mode)</span>}
          </h1>
        </div>
        <div className="page-header-actions">
          <Link to="/" className="btn btn-secondary">
            ← Back Home
          </Link>
          <button 
            className="btn btn-ghost" 
            onClick={() => {
              window.location.href = testMode ? '/products' : '/test/products';
            }}
          >
            🔄 Toggle Mode
          </button>
        </div>
      </div>

      {/* Products Actions Table */}
      <div className="table-actions">
        <Link 
          to={testMode ? "/test/products/new" : "/products/new"} 
          className="btn btn-primary"
        >
          + Add New Product
        </Link>
      </div>

      {/* Products Table */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Product ID</th>
              <th>Name</th>
              <th>Price</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', padding: 'var(--spacing-3xl)' }}>
                  <div className="spinner" style={{ margin: '0 auto' }}></div>
                  <p style={{ marginTop: 'var(--spacing-md)', color: 'var(--text-secondary)' }}>
                    Loading products...
                  </p>
                </td>
              </tr>
            ) : products.length > 0 ? (
              products.map(product => (
                <tr key={product.id}>
                  <td style={{ fontFamily: 'var(--font-family-mono)', fontWeight: 'var(--font-weight-medium)' }}>
                    {product.id}
                  </td>
                  <td style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)' }}>
                    {product.name}
                  </td>
                  <td style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)' }}>
                    ${(product.price / 100).toFixed(2)}
                  </td>
                  <td>
                    <Link 
                      to={testMode ? `/test/products/${product.id}` : `/products/${product.id}`}
                      className="btn btn-sm btn-primary"
                    >
                      View Details
                    </Link>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', padding: 'var(--spacing-3xl)' }}>
                  <div style={{ fontSize: '3rem', marginBottom: 'var(--spacing-lg)' }}>📦</div>
                  <h3 style={{ marginBottom: 'var(--spacing-md)' }}>No Products Found</h3>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    Products will appear here once they are added to the system.
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Summary Card */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Product Summary</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 'var(--spacing-xl)', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 'var(--font-size-3xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--primary-color)' }}>
                {products.length}
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                Total Products
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 'var(--font-size-3xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--success-color)' }}>
                ${(products.reduce((sum, product) => sum + product.price, 0) / 100).toFixed(2)}
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                Total Value
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 'var(--font-size-3xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--info-color)' }}>
                {products.length > 0 ? (products.reduce((sum, product) => sum + product.price, 0) / products.length / 100).toFixed(2) : '0.00'}
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                Average Price
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManageProducts;